import { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid';
import {
  ShippingProviderType,
  type ShipmentSwitchRecovery,
  type SwitchShipmentProviderRequest,
} from '@/infrastructure/OMSClient';
import type { OrderMutationResult } from '@/models/OrderMutationResult';
import {
  clearShipmentSwitchDraft,
  readShipmentSwitchDraft,
  saveShipmentSwitchDraft,
} from '@/pages/oms/orders/utils/shipmentSwitchDraft';
import { SHIPPING_PROVIDER_LABELS } from '@/pages/oms/orders/utils/shippingProviderLabels';

interface SwitchShippingProviderModalProps {
  show: boolean;
  orderId: string;
  sourceProvider: ShippingProviderType;
  sourceProviderOrderId: string;
  operation?: ShipmentSwitchRecovery;
  onHide: () => void;
  onSubmit: (request: SwitchShipmentProviderRequest) => Promise<OrderMutationResult>;
}

type ProviderSelection = ShippingProviderType | '';

const SWITCHABLE_PROVIDERS = [
  ShippingProviderType.DELEO,
  ShippingProviderType.FEDEX,
  ShippingProviderType.PARXL,
  ShippingProviderType.YUN,
] as const;

export function SwitchShippingProviderModal({
  show,
  orderId,
  sourceProvider,
  sourceProviderOrderId,
  operation,
  onHide,
  onSubmit,
}: SwitchShippingProviderModalProps) {
  const [targetProvider, setTargetProvider] = useState<ProviderSelection>('');
  const [confirmation, setConfirmation] = useState('');
  const [reason, setReason] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState(uuidv4());
  const [submitting, setSubmitting] = useState(false);
  const [inputsLocked, setInputsLocked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!show) {
      return;
    }
    const saved = operation ?? readShipmentSwitchDraft(orderId, sourceProviderOrderId);
    setTargetProvider(saved?.targetProvider ?? '');
    setConfirmation('');
    setReason(saved?.reason ?? '');
    setIdempotencyKey(saved?.idempotencyKey ?? uuidv4());
    setInputsLocked(Boolean(saved));
  }, [show, orderId, sourceProviderOrderId, operation]);

  useEffect(() => {
    setError(null);
  }, [show, orderId]);

  const recovering = operation?.state === 'CREATE_FAILED' && operation.recoveryAllowed;
  const blocked = Boolean(
    operation &&
      !recovering &&
      !['PREPARED', 'CANCEL_CONFIRMED', 'CANCEL_UNKNOWN', 'COMPLETED'].includes(operation.state),
  );
  const canSubmit =
    !blocked &&
    Boolean(targetProvider) &&
    targetProvider !== sourceProvider &&
    confirmation.trim() === orderId &&
    Boolean(reason.trim()) &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || !targetProvider) {
      return;
    }
    setSubmitting(true);
    setError(null);

    try {
      const request: SwitchShipmentProviderRequest = {
        targetProvider,
        expectedSourceProviderOrderId: sourceProviderOrderId,
        idempotencyKey,
        reason: reason.trim(),
        ...(recovering ? { retryFailedCreation: true } : {}),
      };
      saveShipmentSwitchDraft(orderId, request);
      setInputsLocked(true);
      const result = await onSubmit(request);

      if (result.success) {
        clearShipmentSwitchDraft(orderId);
        onHide();
        return;
      }

      if (result.preflightRejected) {
        clearShipmentSwitchDraft(orderId);
        setInputsLocked(false);
        setIdempotencyKey(uuidv4());
      }
      setError(result.error ?? 'The provider switch failed. Review the shipment operation state before retrying.');
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : 'The provider switch failed. Review the shipment operation state before retrying.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={() => !submitting && onHide()} backdrop={submitting ? 'static' : true}>
      <Modal.Header closeButton={!submitting}>
        <Modal.Title>Switch Shipping Provider</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant={recovering ? 'warning' : 'danger'}>
          {recovering
            ? 'The source shipment is already cancelled. Correct the order address, then resume FedEx creation with this same operation. The source will not be cancelled again.'
            : 'The target request is validated before cancellation. A new shipment is created only after cancellation is confirmed. Do not retry with a new key when the outcome is uncertain.'}
          <div>
            Source: {sourceProvider} ({sourceProviderOrderId})
          </div>
        </Alert>
        {operation && (
          <Alert variant={blocked ? 'danger' : 'info'}>
            <div>
              Operation {operation.operationId} · {operation.state}
            </div>
            {operation.errorMessage && <div>{operation.errorMessage}</div>}
            {blocked && <div>Manual reconciliation is required before another creation attempt.</div>}
          </Alert>
        )}
        {error && <Alert variant="danger">{error}</Alert>}

        <Form.Group className="mb-3" controlId="switch-provider">
          <Form.Label>Target Provider *</Form.Label>
          <Form.Select
            value={targetProvider}
            onChange={(event) => setTargetProvider(event.target.value as ProviderSelection)}
            disabled={submitting || inputsLocked}
          >
            <option value="">Select a target provider</option>
            {SWITCHABLE_PROVIDERS.filter((provider) => provider !== sourceProvider).map((provider) => (
              <option key={provider} value={provider}>
                {SHIPPING_PROVIDER_LABELS[provider]}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3" controlId="switch-reason">
          <Form.Label>Reason *</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Explain why the provider must be changed"
            disabled={submitting || inputsLocked}
          />
        </Form.Group>

        <Form.Group controlId="switch-confirmation">
          <Form.Label>
            Type <strong>{orderId}</strong> to confirm *
          </Form.Label>
          <Form.Control
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            autoComplete="off"
            disabled={submitting}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="danger" onClick={handleSubmit} disabled={!canSubmit}>
          {submitting
            ? 'Processing shipment operation...'
            : recovering
              ? 'Resume FedEx Creation'
              : 'Confirm Provider Switch'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
